import { formatCopy } from '../../copy/format.js';
import copy from '../../copy/en/auth.json' with { type: 'json' };
import { createClient, type SupabaseClient, type User } from "@supabase/supabase-js";
import type { PortalConfig } from "../lib/config.js";
import { JoinSessionChangedError, joinSessionInstaller, type JoinSessionInstaller } from "./joinSessionInstallation.js";

export type JoinConfirmationResult = "confirmed" | "invalid" | "expired" | "unavailable" | "different-account" | "interrupted";
export type JoinConfirmationStatus = "checking" | JoinConfirmationResult | null;
type Callback = { kind: "session"; accessToken: string; refreshToken: string }
  | { kind: "otp"; tokenHash: string; type: "email" | "signup" };
const credentialKeys = ["access_token", "refresh_token", "type", "code", "token", "token_hash", "token_type", "expires_in", "expires_at", "provider_token", "provider_refresh_token"];
const errorKeys = ["error", "error_code", "error_description"];
const authKeys = [...credentialKeys, ...errorKeys];
const validToken = (value: string | null): value is string => Boolean(value && value.length <= 16_384 && !/\s/.test(value));

export function isJoinConfirmationFailure(status: JoinConfirmationStatus | undefined): status is Exclude<JoinConfirmationResult, "confirmed"> {
  return Boolean(status && status !== "checking" && status !== "confirmed");
}

/** Fixed text only: provider descriptions and recipient details never reach UI. */
export function joinConfirmationNotice(status: JoinConfirmationStatus | undefined): string | null {
  if (!isJoinConfirmationFailure(status)) return null;
  if (status === "different-account") return copy.joinConfirmation.anotherAccountIsSignedInSignOut;
  const reason = status === "expired" ? copy.joinConfirmation.thisConfirmationLinkHasExpiredOrHas
    : status === "unavailable" ? copy.joinConfirmation.weCouldNotReachTheAccountService
      : status === "interrupted" ? copy.joinConfirmation.emailConfirmationWasInterruptedBeforeYourAccount
        : copy.joinConfirmation.thisConfirmationLinkIsIncompleteOrInvalid;
  return formatCopy(copy.joinConfirmation.failureGuidance, { reason });
}

export function createJoinConfirmationClient(config: PortalConfig): SupabaseClient | null {
  if (config.authMode !== "jwt" || !config.supabaseUrl || !config.supabasePublishableKey) return null;
  return createClient(config.supabaseUrl, config.supabasePublishableKey, {
    auth: { persistSession: false, autoRefreshToken: false, detectSessionInUrl: false, storageKey: "ohf-join-confirmation" },
  });
}

function verified(user: User | null): user is User & { email: string } {
  return Boolean(user?.id && user.email && user.email_confirmed_at && !user.is_anonymous);
}

/** Capture once before config loading/routing. Other routes keep their own Auth boundary. */
export function captureJoinConfirmation(url: URL, scrub: () => void): JoinConfirmationAttempt | null {
  if (url.pathname !== "/join" && url.pathname !== "/join/" && !/^\/join\/@[^/]*\/?$/.test(url.pathname)) return null;
  const fragment = new URLSearchParams(url.hash.slice(1));
  const query = url.searchParams;
  if (!authKeys.some(key => fragment.has(key) || query.has(key))) return null;
  // Never retain callback errors, codes or token values in browser history.
  scrub();
  const duplicate = authKeys.some(key => fragment.getAll(key).length + query.getAll(key).length > 1);
  const hasCredentials = credentialKeys.some(key => fragment.has(key) || query.has(key));
  const splitErrors = errorKeys.some(key => fragment.has(key)) && errorKeys.some(key => query.has(key));
  if (!duplicate && !hasCredentials && !splitErrors && (fragment.get("error_code") ?? query.get("error_code")) === "otp_expired") {
    return new JoinConfirmationAttempt(null, "expired");
  }
  if (duplicate || authKeys.some(key => query.has(key)) || ["code", "token", "provider_token", "provider_refresh_token", ...errorKeys].some(key => fragment.has(key))) {
    return new JoinConfirmationAttempt(null);
  }
  const type = fragment.get("type");
  const accessToken = fragment.get("access_token"), refreshToken = fragment.get("refresh_token"), tokenHash = fragment.get("token_hash");
  if (type !== "email" && type !== "signup") return new JoinConfirmationAttempt(null);
  if (validToken(tokenHash) && !["access_token", "refresh_token", "token_type", "expires_in", "expires_at"].some(key => fragment.has(key))) {
    return new JoinConfirmationAttempt({ kind: "otp", tokenHash, type });
  }
  if (!fragment.has("token_hash") && validToken(accessToken) && validToken(refreshToken)
    && (!fragment.has("token_type") || fragment.get("token_type") === "bearer")) {
    return new JoinConfirmationAttempt({ kind: "session", accessToken, refreshToken });
  }
  return new JoinConfirmationAttempt(null);
}

/** One attempt survives StrictMode effect replay without persisting callback credentials. */
export class JoinConfirmationAttempt {
  private initialization: Promise<JoinConfirmationResult> | null = null;
  private cancelled = false;

  constructor(private callback: Callback | null, private invalidResult: "invalid" | "expired" = "invalid") {}

  interrupt(): void { this.cancelled = true; }

  run(client: SupabaseClient | null, createIsolatedClient?: () => SupabaseClient | null): Promise<JoinConfirmationResult> {
    if (this.initialization) return this.initialization;
    const callback = this.callback;
    this.callback = null;
    this.initialization = !callback ? Promise.resolve(this.invalidResult)
      : this.cancelled ? Promise.resolve("interrupted")
        : !client ? Promise.resolve("unavailable") : confirm(callback, client, createIsolatedClient, () => this.cancelled);
    return this.initialization;
  }
}

async function confirm(callback: Callback, client: SupabaseClient, createIsolatedClient?: () => SupabaseClient | null, cancelled: () => boolean = () => false): Promise<JoinConfirmationResult> {
  let interrupted = false;
  let installing = false;
  let recipient: string | null = null;
  let unsubscribe: (() => void) | undefined;
  let isolated: SupabaseClient | null = null;
  let installer: JoinSessionInstaller | null = null;
  let accepted = false;
  try {
    const { data: listener } = client.auth.onAuthStateChange((event, session) => {
    // A callback must not resurrect a session after sign-out or replace an
    // account that signs in while validation is in flight. INITIAL_SESSION is
    // ordinary hydration; the expected SIGNED_IN is this attempt's installation.
    if (event === "SIGNED_OUT" || (event === "SIGNED_IN" && (!installing || session?.user.id !== recipient))) interrupted = true;
    });
    unsubscribe = () => listener.subscription.unsubscribe();
    isolated = createIsolatedClient?.() ?? null;
    if (!isolated || isolated === client) return "unavailable";
    let accessToken: string, refreshToken: string;
    if (callback.kind === "otp") {
      const exchanged = await isolated.auth.verifyOtp({ token_hash: callback.tokenHash, type: callback.type });
      if (interrupted || cancelled()) return "interrupted";
      if (exchanged.error) {
        if (exchanged.error.code === "otp_expired") return "expired";
        return exchanged.error.status && exchanged.error.status < 500 ? "invalid" : "unavailable";
      }
      const received = exchanged.data.session;
      if (!received || !validToken(received.access_token) || !validToken(received.refresh_token)) return "invalid";
      accessToken = received.access_token; refreshToken = received.refresh_token;
    } else {
      accessToken = callback.accessToken; refreshToken = callback.refreshToken;
    }
    // Confirm with Auth rather than trusting either a callback JWT or verifyOtp's
    // embedded user. The isolated exchange cannot overwrite a persisted account.
    const checked = await isolated.auth.getUser(accessToken);
    if (interrupted || cancelled()) return "interrupted";
    if (checked.error || !verified(checked.data.user)) return "expired";
    recipient = checked.data.user.id;
    // Snapshot backing storage before the asynchronous current-session lookup.
    // An undelivered cross-tab change is covered by the installer's comparison.
    installer = joinSessionInstaller(client, checked.data.user, () => interrupted || cancelled());
    if (!installer) return "unavailable";
    const current = await client.auth.getSession();
    if (interrupted || cancelled()) return "interrupted";
    if (current.error) return "unavailable";
    if (current.data.session && current.data.session.user.id !== recipient) return "different-account";

    installing = true;
    const installed = await installer.install({ access_token: accessToken, refresh_token: refreshToken });
    const user = installed.data.session?.user;
    const valid = !installed.error && verified(user ?? null) && user?.id === recipient
      && user.email?.toLowerCase() === checked.data.user.email.toLowerCase();
    const stillCurrent = installer.isCurrent();
    if (interrupted || cancelled() || !valid || !stillCurrent) {
      return interrupted || cancelled() || !stillCurrent ? "interrupted" : "expired";
    }
    accepted = true;
    return "confirmed";
  } catch (error) {
    return error instanceof JoinSessionChangedError ? error.result : interrupted || cancelled() ? "interrupted" : "unavailable";
  } finally {
    await installer?.finish(accepted).catch(() => {});
    if (isolated && isolated !== client) await isolated.auth.dispose().catch(() => {});
    const changed = accepted && installer && !installer.isCurrent();
    unsubscribe?.();
    if (changed) return "interrupted";
  }
}
